import React, { ReactElement, useEffect } from 'react';
import { Box, Chip, Link, List, ListItem, ListItemText, Typography } from '@mui/material';
import { CustomDialog } from '../../components/CustomDialog';
import { useTracking } from '../../analytics/useTracking';
import { RetroCategory, RetroAction } from './analytics/RetroTracking';

export interface RetroBetaInfoDialogProps {
  open: boolean;
  onClosed: () => void;
}

/**
 * Explains the current state of Retro Room to participants: an early beta
 * focused on validating the core retrospective experience, why it's still
 * worth using today (easy export, no lock-in, an anonymous-by-default
 * security model, and how the general privacy policy/terms of service and
 * 90-day data retention apply), and where it's headed (MCP access to retro
 * data for whatever AI tooling a team already uses).
 *
 * Opened from the "Beta" chip in the room's app bar and from the
 * `RetroBetaBanner` shown before creating/joining a room.
 */
export function RetroBetaInfoDialog({ open, onClosed }: RetroBetaInfoDialogProps): ReactElement | null {
  const { track } = useTracking();

  useEffect(() => {
    if (open) {
      track.event(RetroCategory.Room, RetroAction.BetaInfoViewed);
    }
  }, [open, track]);

  if (!open) return null;

  return (
    <CustomDialog open={open} onClosed={onClosed} maxWidth="sm" fullWidth>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Chip label="Beta" size="small" color="info" />
        <Typography variant="h6" fontWeight={700}>Retro Room is an early beta</Typography>
      </Box>

      <Typography variant="body1" sx={{ mb: 2 }}>
        We are focused on getting the core retrospective experience right — real-time
        sticky notes and templates — not the full feature set yet.
      </Typography>

      <List dense sx={{ mb: 1 }}>
        <ListItem sx={{ alignItems: 'flex-start' }}>
          <ListItemText
            primary="No lock-in"
            slotProps={{ primary: { fontWeight: 700 } }}
            secondary="Try it out on a single retrospective — right after you wrap up, export the board as Markdown, plain text, or a PNG from the board menu and take the results with you."
          />
        </ListItem>
        <ListItem sx={{ alignItems: 'flex-start' }}>
          <ListItemText
            primary="Secure, but lightweight"
            slotProps={{ primary: { fontWeight: 700 } }}
            secondary="Just like Story Point Poker, anyone with the link can join anonymously — no account needed. New participants can join while the retro is active; once it wraps up, the board locks so it stays just for the team that was there. Everyone who joined can always get back into the board from the same browser. If the moderator signs in during setup, they can also return to it from anywhere and administer the room via their Recent Retros list."
          />
        </ListItem>
        <ListItem sx={{ alignItems: 'flex-start' }}>
          <ListItemText
            primary="Privacy and terms"
            slotProps={{ primary: { fontWeight: 700 } }}
            secondary={
              <>
                If you sign in, your account is still the same registered Story Point
                Poker account, covered by our{" "}
                <Link underline="always" color="inherit" target="_blank" href="https://storypoint.poker/story-point-poker-privacy-policy.pdf">
                  privacy policy
                </Link>{" "}
                and{" "}
                <Link underline="always" color="inherit" target="_blank" href="https://storypoint.poker/story-point-poker-terms-of-service.pdf">
                  terms of service
                </Link>. Retro Room itself is not part of your subscription plan, and
                where it goes next is still undecided while we are in early beta. So your
                data does not get locked into our systems in the meantime, every board and
                its data is automatically deleted 90 days after the retro is created.
              </>
            }
          />
        </ListItem>
        <ListItem sx={{ alignItems: 'flex-start' }}>
          <ListItemText
            primary="What's next"
            slotProps={{ primary: { fontWeight: 700 } }}
            secondary="If the core experience proves it can stand up to other retro tools while staying as simple as Story Point Poker, our ambition is to keep it that simple — but make it easy to plug into your organisation's AI setup via an MCP server, so you can work with your retro data across your broader tooling."
          />
        </ListItem>
        <ListItem sx={{ alignItems: 'flex-start' }}>
          <ListItemText
            primary="Feedback"
            slotProps={{ primary: { fontWeight: 700 } }}
            secondary={
              <>
                As an early adopter, your feedback shapes where this goes next. We would
                love to hear anything — big or small — about the core retrospective
                experience. Write to us at{" "}
                <Link underline="always" color="inherit" href="mailto:support@storypoint.poker">
                  support@storypoint.poker
                </Link>.
              </>
            }
          />
        </ListItem>
      </List>
    </CustomDialog>
  );
}

export default RetroBetaInfoDialog;
